/**
 * AudioPrefetchService - Intelligently prefetches audio files during idle periods
 *
 * Manages a queue of audio files to prefetch and automatically processes them
 * when the application is idle (no active API requests). Integrates with
 * RequestMonitor for idle detection and Siren for caching.
 */

import Siren from '@/Utils/Siren';

class AudioPrefetchService {
    constructor(config = {}) {
        this.queue = [];
        this.prefetchedFiles = new Set();
        this.isPrefetching = false;
        this.requestMonitor = null;
        this.prefetchDelay = config.prefetchDelay || 1000;
        this.enabled = true;
        this.debug = config.debug || false;
    }

    /**
     * Initialize the service with a request monitor
     * @param {RequestMonitor} requestMonitor - The request monitor instance
     */
    initialize(requestMonitor) {
        if (!requestMonitor) {
            throw new Error('RequestMonitor is required for AudioPrefetchService');
        }

        this.requestMonitor = requestMonitor;

        // Register idle callback
        this.requestMonitor.onIdle(() => {
            this.onIdle();
        });

        this.log('AudioPrefetchService initialized');
    }

    /**
     * Add audio files to the prefetch queue
     * @param {string|string[]} audioFiles - Audio file(s) to prefetch
     */
    addToQueue(audioFiles) {
        const files = Array.isArray(audioFiles) ? audioFiles : [audioFiles];

        files.forEach(file => {
            if (!this.queue.includes(file) && !this.prefetchedFiles.has(file)) {
                this.queue.push(file);
                this.log(`Added to queue: ${file}`);
            }
        });

        this.log(`Queue size: ${this.queue.length}`);

        // Try to start prefetching if idle
        if (this.requestMonitor && this.requestMonitor.isIdle() && !this.isPrefetching) {
            this.onIdle();
        }
    }

    /**
     * Called when system becomes idle
     */
    onIdle() {
        if (!this.enabled || this.isPrefetching || this.queue.length === 0) {
            return;
        }

        this.log('System idle, scheduling prefetch...');

        // Wait a bit before starting prefetch to ensure system is truly idle
        setTimeout(() => {
            this.checkAndPrefetch();
        }, this.prefetchDelay);
    }

    /**
     * Check if still idle and start prefetching
     */
    async checkAndPrefetch() {
        if (!this.requestMonitor.isIdle()) {
            this.log('System no longer idle, canceling prefetch');
            return;
        }

        if (this.queue.length === 0) {
            this.log('Queue empty, nothing to prefetch');
            return;
        }

        this.isPrefetching = true;
        await this.prefetchNext();
        this.isPrefetching = false;
    }

    /**
     * Prefetch the next file in the queue
     */
    async prefetchNext() {
        if (this.queue.length === 0) {
            return;
        }

        const soundFile = this.queue.shift();

        // Check if already cached
        const isCached = await Siren.isCached(soundFile);
        if (isCached) {
            this.prefetchedFiles.add(soundFile);
            this.log(`Already cached: ${soundFile}`);

            // Continue with next file if still idle
            if (this.requestMonitor.isIdle() && this.queue.length > 0) {
                setTimeout(() => this.prefetchNext(), 100);
            }
            return;
        }

        // Prefetch the file
        this.log(`Prefetching: ${soundFile}`);
        try {
            await Siren.preload(soundFile);
            this.prefetchedFiles.add(soundFile);
            this.log(`Successfully prefetched: ${soundFile}`);
        } catch (error) {
            console.error(`Failed to prefetch ${soundFile}:`, error);
            // Don't add to prefetchedFiles on error, allow retry
        }

        // Continue with next file if still idle
        if (this.requestMonitor.isIdle() && this.queue.length > 0) {
            setTimeout(() => this.prefetchNext(), 100);
        }
    }

    /**
     * Enable prefetching
     */
    enable() {
        this.enabled = true;
        this.log('Prefetching enabled');
    }

    /**
     * Disable prefetching
     */
    disable() {
        this.enabled = false;
        this.log('Prefetching disabled');
    }

    /**
     * Check if a file has been prefetched
     * @param {string} soundFile - The sound file to check
     * @returns {boolean} True if file has been prefetched
     */
    isPrefetched(soundFile) {
        return this.prefetchedFiles.has(soundFile);
    }

    /**
     * Get current queue status
     * @returns {Object} Status information
     */
    getStatus() {
        return {
            queueSize: this.queue.length,
            prefetchedCount: this.prefetchedFiles.size,
            isPrefetching: this.isPrefetching,
            enabled: this.enabled,
            isIdle: this.requestMonitor ? this.requestMonitor.isIdle() : false
        };
    }

    /**
     * Clear the queue and reset state
     */
    clear() {
        this.queue = [];
        this.prefetchedFiles.clear();
        this.isPrefetching = false;
        this.log('Service cleared');
    }

    /**
     * Log debug messages
     * @param {string} message - Message to log
     */
    log(message) {
        if (this.debug) {
            console.log(`[AudioPrefetchService] ${message}`);
        }
    }
}

export default AudioPrefetchService;
